import { ConfigurationService } from '../../shared/services/configuration.service';
import { LazyLoadEvent } from 'primeng/api';
import { PortfolioService } from '../shared/services/portfolio.service';
import { PortfolioCustomerDetailsFilterModel } from '../shared/models/portfolio-customer-details-filter-model';
import { PagingFilter } from '../../shared/models/paging-filter.model';
import { PortfolioCustomerDetailsModel } from '../shared/models/portfolio-customer-details-model';
import { PortfolioModel } from '../shared/models/portfolio-model';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CommentsService } from '../../shared/services/comments.service';
import { ThreadRelatedObjectType } from '../../shared/models/comment-models/thread-related-object-type.enum';
import { ThreadTopic } from '@admin/shared/models/comment-models/thread-topic.enum';
import { PostingChannel } from '@admin/shared/models/comment-models/posting-channel.enum';
import { Table } from 'primeng/table';

@Component({
  selector: 'hr-portfolio-customer-details',
  templateUrl: './portfolio-customer-details.component.html',
  styleUrls: ['./portfolio-customer-details.component.less'],
  standalone: false,
})
export class PortfolioCustomerDetailsComponent implements OnInit {
  public displayMove: boolean;

  public customerDetails: PortfolioCustomerDetailsModel[];
  public selectedCustomers: PortfolioCustomerDetailsModel[];

  @Input('portfolio')
  public portfolio: PortfolioModel;

  public filterPagingWrapper: PagingFilter<PortfolioCustomerDetailsFilterModel>;
  public totalRecords: number;
  public isReset: boolean;

  public dateFormat: string;
  public specialDateFormat: string;
  public pageSize: number;
  public decimalFormat: string;

  public ThreadTopicEnum = ThreadTopic;
  public ThreadRelatedObjectTypeEnum = ThreadRelatedObjectType;
  public PostingChannelEnum = PostingChannel;
  public ColumnGroupEnum = ColumnGroupEnum;

  public displayedOrEditingThreadId: number;
  public editingThreadObjectId: number;
  public displayedOrEditingThreadTopic: number;
  public displayThread: boolean;
  public displayThreadPostEditor: boolean;

  public columnGroups: any[];
  public selectedColumnGroups: any[];
  public columnGroupsVisibility: { [group: number]: boolean };

  public customPageNumber: number;
  @ViewChild(Table, { static: true }) tableComponent: Table;

  onLoad(event: LazyLoadEvent) {
    if (this.isReset) {
      this.customerDetails = [];
      this.totalRecords = 0;
      return;
    }

    if (event.sortField && event.sortOrder) {
      this.filterPagingWrapper.sortParameters = [
        {
          column: event.sortField,
          order: event.sortOrder == 1 ? 'desc' : 'asc',
        },
      ];
    }

    this.filterPagingWrapper.pagingInfo.start = event.first;
    this.filterPagingWrapper.pagingInfo.limit = event.rows;
    this.customPageNumber = event.first / event.rows + 1;
    this.onFilter();
  }

  onFilter() {
    this.filterPagingWrapper.pagingInfo.start =
      this.filterPagingWrapper.pagingInfo.start || 0;

    this.portfolioService
      .listPortfolioCustomerDetails(
        this.portfolio.portfolioId,
        this.filterPagingWrapper
      )
      .subscribe((d) => {
        this.totalRecords = d.totalCount;
        this.customerDetails = d.items;

        this.loadComments();
      });
  }

  onCustomPageEnter() {
    if (this.customPageNumber < 1) this.customPageNumber = 1;

    let first = (this.customPageNumber - 1) * this.tableComponent.rows;
    if (first > this.totalRecords - this.tableComponent.rows)
      first =
        this.totalRecords - (this.totalRecords % this.tableComponent.rows);

    this.tableComponent.first = first;
  }

  loadComments() {
    if (!this.customerDetails || this.customerDetails.length == 0) return;

    var customerIds = this.customerDetails.map((c) => c.customerId);
    this.commentsService
      .FilterThreadPreviews(ThreadRelatedObjectType.Customer, customerIds)
      .subscribe((d) => {
        let itemsDict = d.items;
        this.customerDetails.forEach((c) => {
          let customerThreads = itemsDict[c.customerId];
          if (customerThreads) {
            customerThreads.forEach((ct) => {
              c.lastComments[ct.threadTopicId] = ct.posts[0]; // take latest one, channel doesn't metter
            });
          }
        });
      });
  }

  showThread(threadId: number, topicId: number) {
    this.displayedOrEditingThreadId = threadId;
    this.displayedOrEditingThreadTopic = topicId;
    this.displayThread = true;
  }

  showCommentEditor(threadId: number, objectId: number, topicId: number) {
    this.displayedOrEditingThreadId = threadId;
    this.editingThreadObjectId = objectId;
    this.displayedOrEditingThreadTopic = topicId;
    this.displayThreadPostEditor = true;
  }

  onApplyClick(dt: Table) {
    this.filterPagingWrapper.pagingInfo.start = 0;

    //TODO: bug in paginator, does not update state correctly, change this code after bug is fixed in framework (primeng)
    this.isReset = true;
    dt.reset();
    this.isReset = false;

    this.onFilter();
  }

  onClearClick() {
    this.filterPagingWrapper = new PagingFilter(
      new PortfolioCustomerDetailsFilterModel()
    );
  }

  onCompanyMove() {
    this.displayMove = true;
  }

  public onUpdateTable(updated: boolean) {
    this.displayMove = false;
    this.displayThreadPostEditor = false;
    this.displayThread = false;
    if (updated) {
      this.selectedCustomers = [];
      this.onFilter();
    }
  }

  public onThreadClose(switchToAdd: boolean) {
    this.displayThread = false;
    if (switchToAdd) this.displayThreadPostEditor = true;
  }

  public onPostDelete(deleted: boolean) {
    this.onFilter();
  }

  getSpecialDateFormat(format: string) {
    return format.replace('MM', 'mm').replace('yyyy', 'yy');
  }

  refreshColumnsVisibility() {
    this.columnGroupsVisibility[ColumnGroupEnum.CustomerInfo] =
      this.selectedColumnGroups.filter(
        (g) => g.field == ColumnGroupEnum.CustomerInfo
      ).length > 0;
    this.columnGroupsVisibility[ColumnGroupEnum.Quantity] =
      this.selectedColumnGroups.filter(
        (g) => g.field == ColumnGroupEnum.Quantity
      ).length > 0;
    this.columnGroupsVisibility[ColumnGroupEnum.Amount] =
      this.selectedColumnGroups.filter((g) => g.field == ColumnGroupEnum.Amount)
        .length > 0;
    this.columnGroupsVisibility[ColumnGroupEnum.Comments] =
      this.selectedColumnGroups.filter(
        (g) => g.field == ColumnGroupEnum.Comments
      ).length > 0;
  }

  onColumnsDropdownChange(event: any) {
    this.refreshColumnsVisibility();
  }

  constructor(
    private portfolioService: PortfolioService,
    private commentsService: CommentsService,
    private config: ConfigurationService
  ) {
    this.filterPagingWrapper = new PagingFilter(
      new PortfolioCustomerDetailsFilterModel()
    );
    this.selectedCustomers = [];
    this.displayMove = false;
    this.displayThread = false;
    this.displayThreadPostEditor = false;
    this.columnGroupsVisibility = {};
  }

  ngOnInit() {
    let config = this.config.getCachedConfig();

    this.dateFormat = config.dateFormat;
    this.specialDateFormat = this.getSpecialDateFormat(this.dateFormat);
    this.pageSize = config.defaultPageSize;

    this.decimalFormat = '1.2-2';

    this.columnGroups = [
      { field: ColumnGroupEnum.CustomerInfo, header: 'Customer info' },
      { field: ColumnGroupEnum.Quantity, header: 'Quantity' },
      { field: ColumnGroupEnum.Amount, header: 'Amount' },
      { field: ColumnGroupEnum.Comments, header: 'Comments' },
    ];
    this.selectedColumnGroups = this.columnGroups.filter(
      (g) => g.field != ColumnGroupEnum.Comments
    ); // comments hidden by default
    this.refreshColumnsVisibility();
  }
}

enum ColumnGroupEnum {
  CustomerInfo = 1,
  Quantity = 2,
  Amount = 3,
  Comments = 4,
}
